import React from "react";
import { ArrowUpRight, Heart } from "lucide-react";
import { useI18n } from "@/i18n";

/**
 * Partner / friends-of-the-site section.
 * Each entry: { name, tagline, description, url, image, badge, badgeIt }
 */
const FRIENDS = [
  {
    id: "rotta-principe",
    name: "La Rotta del Principe",
    url: "https://www.larottadelprincipe.com",
    image: "https://larottadelprincipe.com/assets/poster-1164.webp",
    badge: "Documentary · 2026",
    badgeIt: "Documentario · 2026",
    descriptionIt:
      "Un documentario di Fausto Farinelli: Pietro Moro ripercorre il viaggio del 1961 alle origini della Costa Smeralda. Una storia che ha cambiato la Gallura.",
    descriptionEn:
      "A documentary by Fausto Farinelli: Pietro Moro retraces the 1961 voyage to the origins of Costa Smeralda. A story that changed Gallura.",
    taglineIt: "Il mare. La voce. La memoria.",
    taglineEn: "The sea. The voice. The memory.",
  },
];

const COPY = {
  it: {
    eyebrow: "Amici del sito",
    title: "Chi condivide la nostra passione per la Gallura",
    lead:
      "Progetti e realtà del territorio che custodiscono e raccontano l'anima autentica della Sardegna del Nord. Diamo loro un piccolo spazio qui perché meritano di essere scoperti.",
    visit: "Visita il sito",
  },
  en: {
    eyebrow: "Friends of the site",
    title: "Who shares our passion for Gallura",
    lead:
      "Projects and local businesses that preserve and tell the authentic soul of Northern Sardinia. We give them a small space here because they deserve to be discovered.",
    visit: "Visit the site",
  },
};

export default function FriendsSection() {
  const { lang } = useI18n();
  const c = COPY[lang] || COPY.it;

  return (
    <section
      id="friends"
      data-testid="friends-section"
      className="bg-[#F9F7F3] py-20 lg:py-24"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="max-w-3xl">
          <div className="flex items-center gap-2 uppercase tracking-[0.3em] text-xs text-slate-500 mb-4">
            <Heart className="w-3.5 h-3.5 accent-text fill-current" />
            <span>{c.eyebrow}</span>
          </div>
          <h2 className="font-display text-3xl md:text-4xl brand-text leading-tight">
            {c.title}
          </h2>
          <p className="mt-5 text-slate-700 leading-relaxed text-base md:text-lg">
            {c.lead}
          </p>
        </div>

        {/* Partners grid */}
        <div className="mt-12 grid md:grid-cols-2 gap-6">
          {FRIENDS.map((f) => (
            <a
              key={f.id}
              href={f.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`friend-card-${f.id}`}
              className="group bg-white rounded-2xl overflow-hidden border border-slate-200 hover:border-slate-400 hover:shadow-xl transition-all duration-300 flex flex-col sm:flex-row"
            >
              {/* Image */}
              <div className="sm:w-2/5 overflow-hidden bg-slate-100 relative">
                <img
                  src={f.image}
                  alt={f.name}
                  className="w-full h-full object-cover aspect-video sm:aspect-auto group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </div>

              {/* Content */}
              <div className="sm:w-3/5 p-6 md:p-7 flex flex-col justify-between gap-4">
                <div>
                  <div className="text-[10px] uppercase tracking-widest accent-text font-medium mb-2">
                    {lang === "it" ? f.badgeIt : f.badge}
                  </div>
                  <h3 className="font-display text-xl md:text-2xl brand-text leading-tight group-hover:underline underline-offset-4 decoration-2">
                    {f.name}
                  </h3>
                  <p className="font-display italic text-sm text-slate-500 mt-1">
                    {lang === "it" ? f.taglineIt : f.taglineEn}
                  </p>
                  <p className="text-sm text-slate-600 leading-relaxed mt-4">
                    {lang === "it" ? f.descriptionIt : f.descriptionEn}
                  </p>
                </div>
                <div className="inline-flex items-center gap-1 text-sm font-medium brand-text">
                  {c.visit}
                  <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}
